import React, { useEffect, useState } from 'react';
import { SearchIcon, ChevronDownIcon, ArrowLeftIcon } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useLocation } from 'react-router-dom';
// Sample service data organized by department and section
const allServicesByDepartment = {
  'land-property': {
    departmentName: 'Land & Property',
    sections: {
      'Land Title Services': [{
        id: 'lt1',
        title: 'New Land Title Registration',
        description: 'Apply for first-time registration of land title',
        category: 'Land Title Services',
        estimatedTime: '15-20 business days',
        fee: '$250.00'
      }, {
        id: 'lt2',
        title: 'Land Title Transfer',
        description: 'Transfer land ownership through sale, inheritance, or gift',
        category: 'Land Title Services',
        estimatedTime: '10-15 business days',
        fee: '$200.00'
      }, {
        id: 'lt3',
        title: 'Title Rectification',
        description: 'Apply for correction of land title information',
        category: 'Land Title Services',
        estimatedTime: '5-10 business days',
        fee: '$150.00'
      }, {
        id: 'lt4',
        title: 'Land Sub-division',
        description: 'Application for subdividing land title',
        category: 'Land Title Services',
        estimatedTime: '20-25 business days',
        fee: '$300.00'
      }, {
        id: 'lt5',
        title: 'Land Amalgamation',
        description: 'Combine multiple land titles into one',
        category: 'Land Title Services',
        estimatedTime: '20-25 business days',
        fee: '$300.00'
      }, {
        id: 'lt6',
        title: 'Lease Registration',
        description: 'Register a new land lease agreement',
        category: 'Land Title Services',
        estimatedTime: '7-10 business days',
        fee: '$175.00'
      }],
      'Property Development': [{
        id: 'pd1',
        title: 'Building Permit - New Construction',
        description: 'Apply for permit to construct new buildings',
        category: 'Property Development',
        estimatedTime: '15-20 business days',
        fee: '$500.00'
      }, {
        id: 'pd2',
        title: 'Renovation Permit',
        description: 'Get approval for property renovations or extensions',
        category: 'Property Development',
        estimatedTime: '10-15 business days',
        fee: '$250.00'
      }, {
        id: 'pd3',
        title: 'Demolition Permit',
        description: 'Apply for permission to demolish existing structures',
        category: 'Property Development',
        estimatedTime: '7-10 business days',
        fee: '$200.00'
      }],
      'Information Requests': [{
        id: 'ir1',
        title: 'Official Land Search',
        description: 'Request official land title verification',
        category: 'Information Requests',
        estimatedTime: '2-3 business days',
        fee: '$50.00'
      }, {
        id: 'ir2',
        title: 'Cadastral Map Request',
        description: 'Get detailed property boundary information',
        category: 'Information Requests',
        estimatedTime: '3-5 business days',
        fee: '$75.00'
      }, {
        id: 'ir3',
        title: 'Survey Data Request',
        description: 'Access property survey information',
        category: 'Information Requests',
        estimatedTime: '3-5 business days',
        fee: '$100.00'
      }],
      'Permits & Certificates': [{
        id: 'pc1',
        title: 'Occupancy Certificate',
        description: 'Apply for building occupancy certification',
        category: 'Permits & Certificates',
        estimatedTime: '5-7 business days',
        fee: '$150.00'
      }, {
        id: 'pc2',
        title: 'Hoarding Permit',
        description: 'Get permission for temporary construction barriers',
        category: 'Permits & Certificates',
        estimatedTime: '3-5 business days',
        fee: '$100.00'
      }, {
        id: 'pc3',
        title: 'Property Address Assignment',
        description: 'Request official property address confirmation',
        category: 'Permits & Certificates',
        estimatedTime: '5-7 business days',
        fee: '$50.00'
      }]
    }
  },
  'taxes-revenue': {
    departmentName: 'Taxes & Revenue',
    sections: {
      'Property Tax': [{
        id: 'pt1',
        title: 'Property Tax Payment',
        description: 'Pay your annual property tax',
        category: 'Property Tax',
        estimatedTime: 'Immediate',
        fee: 'Varies'
      }, {
        id: 'pt2',
        title: 'Property Tax Appeal',
        description: 'Appeal your property tax assessment',
        category: 'Property Tax',
        estimatedTime: '30-45 business days',
        fee: '$100.00'
      }],
      'Business Tax': [{
        id: 'bt1',
        title: 'Business Tax Filing',
        description: 'File your annual business tax return',
        category: 'Business Tax',
        estimatedTime: 'Immediate',
        fee: 'Varies'
      }]
    }
  }
  // Add more departments as needed
};
export const ServiceCataloguePage = () => {
  const {
    navigate
  } = useNavigation();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  // Extract department from URL path
  const pathParts = location.pathname.split('/');
  const department = pathParts[1]; // e.g., 'land-property'
  const departmentData = allServicesByDepartment[department as keyof typeof allServicesByDepartment];
  const departmentName = departmentData?.departmentName || 'Services';
  const servicesBySection = departmentData?.sections || {};
  const [expandedSections, setExpandedSections] = useState<string[]>(Object.keys(servicesBySection));
  // Update expanded sections when department changes
  useEffect(() => {
    setExpandedSections(Object.keys(servicesBySection));
  }, [department]);
  const toggleSection = (section: string) => {
    setExpandedSections(current => current.includes(section) ? current.filter(s => s !== section) : [...current, section]);
  };
  // Get all categories for the current department
  const allCategories = Object.values(servicesBySection).flat().map(service => service.category);
  const uniqueCategories = [...new Set(allCategories)];
  // Filter services based on search query and selected category
  const filteredSections = Object.entries(servicesBySection).reduce((acc, [section, services]) => {
    const filteredServices = services.filter(service => (selectedCategory === 'all' || service.category === selectedCategory) && (service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase())));
    if (filteredServices.length > 0) {
      acc[section] = filteredServices;
    }
    return acc;
  }, {} as typeof servicesBySection);
  const handleServiceSelect = (serviceId: string) => {
    navigate(`/assisted-creation/identify-citizen/${serviceId}`);
  };
  const handleBackClick = () => {
    navigate(`/${department}`);
  };
  return <div className="container mx-auto px-4 py-8 max-w-7xl" data-id="element-4846">
      <div className="mb-8" data-id="element-4847">
        <button onClick={handleBackClick} className="flex items-center text-blue-600 hover:text-blue-800 mb-4" data-id="element-4848">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4849" />
          <span data-id="element-4850">Back to {departmentName}</span>
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4851">
          {departmentName} Service Catalogue
        </h1>
        <p className="text-gray-600" data-id="element-4852">
          Browse and select from our available {departmentName.toLowerCase()}{' '}
          services
        </p>
      </div>
      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200" data-id="element-4853">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-4854">
          <div className="flex-1 relative" data-id="element-4855">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-4856" />
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4857" />
          </div>
          {uniqueCategories.length > 0 && <div className="w-full md:w-64" data-id="element-4858">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-4859">
                <option value="all" data-id="element-4860">All Categories</option>
                {uniqueCategories.map(category => <option key={category} value={category} data-id="element-4861">
                    {category}
                  </option>)}
              </select>
            </div>}
        </div>
      </div>
      {/* Services List */}
      <div className="space-y-8" data-id="element-4862">
        {Object.keys(filteredSections).length > 0 ? Object.entries(filteredSections).map(([section, services]) => <div key={section} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200" data-id="element-4863">
              <button className="flex items-center justify-between w-full text-left mb-4" onClick={() => toggleSection(section)} data-id="element-4864">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-4865">
                  {section}
                </h3>
                <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${expandedSections.includes(section) ? 'rotate-180' : ''}`} data-id="element-4866" />
              </button>
              {expandedSections.includes(section) && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4867">
                  {services.map(service => <button key={service.id} className="text-left p-5 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group relative overflow-hidden bg-white" onClick={() => handleServiceSelect(service.id)} data-id="element-4868">
                      <div className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-10 transition-opacity duration-200" data-id="element-4869"></div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-blue-700 transition-colors duration-200" data-id="element-4870">
                        {service.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-5 line-clamp-2" data-id="element-4871">
                        {service.description}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500" data-id="element-4872">
                        <div className="flex items-center space-x-4" data-id="element-4873">
                          <span className="flex items-center bg-gray-50 px-2 py-1 rounded-md" data-id="element-4874">
                            <span className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-4875">
                              ⏱
                            </span>
                            <span className="font-medium" data-id="element-4876">
                              {service.estimatedTime}
                            </span>
                          </span>
                          <span className="flex items-center bg-gray-50 px-2 py-1 rounded-md" data-id="element-4877">
                            <span className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-4878">
                              💰
                            </span>
                            <span className="font-medium" data-id="element-4879">{service.fee}</span>
                          </span>
                        </div>
                      </div>
                    </button>)}
                </div>}
            </div>) : <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-200 text-center" data-id="element-4880">
            <p className="text-gray-500" data-id="element-4881">
              No services found matching your criteria.
            </p>
          </div>}
      </div>
    </div>;
};